/**
 * Hook personnalisé pour la génération des nœuds et edges du ChatFlowDiagram
 * Ce fichier contient la logique complexe de génération de la structure visuelle
 */
import { useEffect } from 'react';
import React from 'react';
import { Position } from '@xyflow/react';
import { NodeLabel, ChatFlowStartLabel, GreetingLabel } from '../components/NodeLabel';
import { getColor, calculateRoutingWidth, findParent, calculateLevel } from '../utils/nodeUtils';
import { 
  createChatFlowStartNode, 
  createGreetingNode, 
  createRoutingNode,
  createQueueNode
} from '../utils/nodeCreators';
import { 
  createMissingChildNodesRecursive, 
  repositionChildNodesRecursive 
} from '../utils/nodePositioning';

/**
 * Hook pour générer les nœuds et edges à partir des données ChatFlow
 */
export const useNodeGeneration = ({
  chatFlowData,
  setNodes,
  setEdges,
  handleEditNode,
  handleDeleteNode,
  handleEdgeDelete,
  handleEdgeLabelChange,
  reactFlowInstance
}) => {
  
  useEffect(() => {
    if (!chatFlowData) return;

    // Créer des fonctions inline pour l'accès dans les composants
    const inlineHandleEditNode = (nodeId) => {
      handleEditNode(nodeId);
    };

    const inlineHandleDeleteNode = (nodeId) => {
      if (nodeId === 'routing-0') {
        console.warn('⚠️ La suppression du routing "0" est interdite');
        return;
      }
      handleDeleteNode(nodeId);
    };

    const inlineHandleEdgeDelete = (edgeId) => {
      handleEdgeDelete(edgeId);
    };

    const inlineHandleEdgeLabelChange = (edgeId, newLabel) => {
      handleEdgeLabelChange(edgeId, newLabel);
    };

    const generatedNodes = [];
    const generatedEdges = [];
    
    // Constantes de positionnement
    const xPosition = 400;
    let yPosition = 50;
    const verticalGap = 180;
    const horizontalGap = 450;
    const nodeWidth = 200;
    const minSpacing = 50;


    // 1. Nœud ChatFlow Start
    generatedNodes.push(createChatFlowStartNode(xPosition, yPosition, inlineHandleEditNode));
    yPosition += verticalGap;

    // 2. Nœud Greeting (si des messages sont activés)
    const greeting = chatFlowData.greeting || {};
    const enabledMessages = Object.entries(greeting).filter(([key, msg]) => msg?.enabled);
    
    let lastNodeId = 'chatflow-start';
    
    if (enabledMessages.length > 0) {
      generatedEdges.push({
        id: 'e-chatflow-greeting',
        type: 'custom',
        source: 'chatflow-start',
        target: 'greeting',
        animated: true,
        style: { stroke: '#5b5fc7', strokeWidth: 2 },
        markerEnd: { type: 'arrowclosed', color: '#5b5fc7' },
        data: { onDelete: inlineHandleEdgeDelete, onLabelChange: inlineHandleEdgeLabelChange },
      });

      generatedNodes.push(createGreetingNode(
        xPosition, 
        yPosition, 
        enabledMessages.length,
        inlineHandleEditNode,
        inlineHandleDeleteNode
      ));

      yPosition += verticalGap;
      lastNodeId = 'greeting';
    }

    // 3. Gestion des routings
    const routing = chatFlowData.routing || {};
    const routing0Y = yPosition;
    
    // Identifier les routings utilisés comme "next"
    const routingsUsedAsNext = new Set();
    Object.entries(routing).forEach(([key, r]) => {
      if ((r.type?.toLowerCase() === 'customer choice' || r.type?.toLowerCase() === 'customerchoice') && r.customerChoiceActions) {
        Object.values(r.customerChoiceActions).forEach(action => {
          if (action.next?.routingName) {
            routingsUsedAsNext.add(action.next.routingName);
          }
        });
      }
      if (r.type?.toLowerCase() === 'form' && r.next?.routingName) {
        routingsUsedAsNext.add(r.next.routingName);
      }
    });
    
    // Filtrer les routings à afficher (enabled et non-utilisés comme next, sauf "0")
    const routingEntries = Object.entries(routing).filter(([key, r]) => {
      const isEnabled = key === "0" || r?.enabled !== false;
      const isUsedAsNext = routingsUsedAsNext.has(key) && key !== "0";
      return isEnabled && !isUsedAsNext;
    });
    
    
    // Calculer les niveaux hiérarchiques
    const routingLevels = new Map();
    const routingParents = new Map();
    const routingMap = Object.fromEntries(routingEntries);
    
    const routing0 = routingEntries.find(([key]) => key === "0");
    const otherRoutings = routingEntries.filter(([key]) => key !== "0");
    
    if (routing0) {
      routingLevels.set("0", 0);
      calculateLevel("0", routingMap, routingLevels, 0);
    }
    
    // Calculer les niveaux pour les autres routings
    otherRoutings.forEach(([key]) => {
      if (!routingLevels.has(key)) {
        const parent = findParent(key, routingMap);
        routingParents.set(key, parent);
        if (parent) {
          const parentLevel = routingLevels.get(parent) || 0;
          routingLevels.set(key, parentLevel + 1);
        } else {
          routingLevels.set(key, 0);
        }
      } else {
        const parent = findParent(key, routingMap);
        routingParents.set(key, parent);
      }
    });
    
    // Grouper par niveau
    const routingsByLevel = new Map();
    otherRoutings.forEach(([key]) => {
      const level = routingLevels.get(key) || 0;
      if (!routingsByLevel.has(level)) {
        routingsByLevel.set(level, []);
      }
      routingsByLevel.get(level).push(key);
    });

    // Calculer les positions pour le niveau 0
    const level0Routings = [];
    if (routing0) level0Routings.push(routing0);
    
    otherRoutings.forEach(([key]) => {
      const level = routingLevels.get(key);
      const parent = routingParents.get(key);
      if ((level === 0 || !routingLevels.has(key)) && !parent) {
        level0Routings.push([key, routing[key]]);
      }
    });
    
    
    // Calculer les largeurs
    const level0Widths = new Map();
    level0Routings.forEach(([key, routingItem]) => {
      const width = calculateRoutingWidth(key, routingItem, nodeWidth);
      level0Widths.set(key, width);
    });
    
    // Calculer les positions X pour niveau 0
    const level0Positions = new Map();
    level0Positions.set("0", xPosition);
    
    const otherLevel0Routings = level0Routings.filter(([k]) => k !== "0");
    const leftRoutings = otherLevel0Routings.slice(0, Math.floor(otherLevel0Routings.length / 2));
    const rightRoutings = otherLevel0Routings.slice(Math.floor(otherLevel0Routings.length / 2));
    
    const routing0Width = level0Widths.get("0") || nodeWidth;
    
    // Positions à gauche
    leftRoutings.forEach(([key], index) => {
      let accumulatedOffset = routing0Width / 2 + minSpacing;
      
      for (let i = leftRoutings.length - 1; i > index; i--) {
        const [prevKey] = leftRoutings[i];
        const prevWidth = level0Widths.get(prevKey) || nodeWidth;
        accumulatedOffset += prevWidth + minSpacing;
      }
      
      const currentWidth = level0Widths.get(key) || nodeWidth;
      accumulatedOffset += currentWidth / 2;
      
      level0Positions.set(key, xPosition - accumulatedOffset);
    });
    
    // Positions à droite
    rightRoutings.forEach(([key], index) => {
      let accumulatedOffset = routing0Width / 2 + minSpacing;
      
      for (let i = 0; i < index; i++) {
        const [prevKey] = rightRoutings[i];
        const prevWidth = level0Widths.get(prevKey) || nodeWidth;
        accumulatedOffset += prevWidth + minSpacing;
      }
      
      const currentWidth = level0Widths.get(key) || nodeWidth;
      accumulatedOffset += currentWidth / 2;
      
      level0Positions.set(key, xPosition + accumulatedOffset);
    });

    // Créer le routing "0" et son edge
    if (routing0) {
      const [key, routingItem] = routing0;
      const nodeData = createRoutingNode(
        key,
        routingItem,
        xPosition,
        routing0Y,
        inlineHandleEditNode,
        inlineHandleDeleteNode,
        chatFlowData
      );
      generatedNodes.push(nodeData.node);
      
      // Edge depuis chatflow-start ou greeting vers routing-0
      generatedEdges.push({
        id: `e-${lastNodeId}-routing-${key}`,
        type: 'custom',
        source: lastNodeId,
        target: `routing-${key}`,
        animated: true,
        style: { stroke: '#5b5fc7', strokeWidth: 2 },
        markerEnd: { type: 'arrowclosed', color: '#5b5fc7' },
        data: { onDelete: inlineHandleEdgeDelete, onLabelChange: inlineHandleEdgeLabelChange },
      });
    }

    // Créer tous les autres routings de niveau 0 (sans parent)
    otherLevel0Routings.forEach(([key, routingItem]) => {
      const nodeX = level0Positions.get(key) || xPosition;
      const nodeData = createRoutingNode(
        key,
        routingItem,
        nodeX,
        routing0Y,
        inlineHandleEditNode,
        inlineHandleDeleteNode,
        chatFlowData
      );
      generatedNodes.push(nodeData.node);
    });

    // Création récursive des nœuds manquants
    const existingNodeIds = [...generatedNodes.map(n => n.id)];
    existingNodeIds.forEach(nodeId => {
      if (nodeId.startsWith('routing-')) {
        const key = nodeId.replace('routing-', '');
        const node = generatedNodes.find(n => n.id === nodeId);
        if (node) {
          createMissingChildNodesRecursive(
            key, 
            node, 
            routing,
            generatedNodes,
            generatedEdges,
            verticalGap,
            horizontalGap,
            inlineHandleEditNode,
            inlineHandleDeleteNode,
            inlineHandleEdgeDelete,
            inlineHandleEdgeLabelChange,
            chatFlowData,
            0
          );
        }
      }
    });
    
    // Repositionnement récursif
    Object.keys(chatFlowData.routing || {}).forEach(key => {
      const nodeId = `routing-${key}`;
      const node = generatedNodes.find(n => n.id === nodeId);
      if (node) {
        repositionChildNodesRecursive(
          key,
          node,
          chatFlowData,
          generatedNodes,
          verticalGap,
          horizontalGap,
          0
        );
      }
    });

    
    setNodes(generatedNodes);
    setEdges(generatedEdges);
    
    // Ajuster la vue
    setTimeout(() => {
      if (reactFlowInstance) {
        reactFlowInstance.fitView({ padding: 0.2, duration: 800 });
      }
    }, 100);
    
  }, [chatFlowData, setNodes, setEdges, reactFlowInstance]);
  // Note: handleEditNode, handleDeleteNode, handleEdgeDelete, handleEdgeLabelChange ne sont pas dans les dépendances
  // car ils sont utilisés uniquement dans les closures des composants React créés
};
